import React, {useEffect, useState} from "react";
import {useField, useFormikContext} from "formik";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import {copyrightTypeValueToHr} from "../utility";

/**
 * FormikSelect is a customized select dropdown component designed to work seamlessly with Formik.
 * It provides integration with Formik's field management and validation, allowing dynamic handling
 * of select values and rendering of human-readable values for specific fields.
 *
 * @param {Object} props - Props passed to the component.
 * @param {string} props.formikKey - The key of the field in Formik's form state with which the component is associated.
 * @param {string} props.label - The label text displayed above the select dropdown.
 * @param {boolean} props.fullWidth - If `true`, the component will span the full width of its container.
 * @param {Function} props.t - (Optional) A translation function used to render human-readable values for specific fields.
 * @param {Array} [props.valuesList=[]] - An optional array of selectable values. Each value is typically an object with an `id` field.
 * @param {React.ReactNode} props.children - The child components defining individual options within the select dropdown.
 * @param {Object} props.props - Additional properties passed to the underlying `Select` component from Material-UI.
 *
 * @returns {JSX.Element} A React component that renders a Formik-integrated select dropdown with Formik-based validation and field management.
 */
export const FormikSelect = ({
                                 formikKey,
                                 label,
                                 fullWidth,
                                 t,
                                 valuesList = [],
                                 children,
                                 ...props
                             }) => {
    const {setFieldValue} = useFormikContext();
    const [field, meta] = useField(formikKey);

    const [loading, setLoading] = useState(true);
    const [defaultValue, setDefaultValue] = useState('');

    /**
     * Hook used await the loading of the field's default value,
     * before proceeding with the rest of the component
     */
    useEffect(() => {
        setDefaultValue(field.value);
        setLoading(false);
    }, [field.value]);

    /**
     * Renders the value of the select field, translated to human-readable representation.
     * If the field is 'copyrightType', the value is translated to human-readable using
     * the 'copyrightTypeValueToHr' function.
     *
     * @returns {string} The rendered value of the select field.
     */
    const valueRenderer = () => {
        if (formikKey === 'copyrightType' && typeof (t) !== 'undefined') {
            return copyrightTypeValueToHr(t, field.value, true);
        }
    };

    const getValue = () => {
        if (typeof (field.value?.id) !== 'undefined') {
            return field.value?.id;
        }
        return field.value;
    };

    const getDefaultValue = () => {
        if (typeof (defaultValue?.id) !== 'undefined') {
            return defaultValue?.id;
        }
        return defaultValue;
    };

    const onChange = (event) => {
        const selectedValue = event.target.value;
        if (valuesList.length > 0) {
            const selectedOption = valuesList.find((option) => option?.id === selectedValue);
            if (selectedOption) {
                setFieldValue(field.name, selectedOption).then();
            }
        } else {
            setFieldValue(field.name, selectedValue).then();
        }
    };

    return (!loading &&
        <FormControl fullWidth={fullWidth}>
            <InputLabel id={`${field.name}-select-label`}>{label}</InputLabel>
            <Select
                id={field.name}
                labelId={`${field.name}-select-label`}
                name={field.name}
                error={meta.touched && Boolean(meta.error)}
                value={getValue()}
                defaultValue={getDefaultValue()}
                onChange={onChange}
                onBlur={field.onBlur}
                renderValue={formikKey === 'copyrightType' ? valueRenderer : undefined}
                {...props}
            >
                {children}
            </Select>
            <FormHelperText>{meta.touched ? meta.error : ""}</FormHelperText>
        </FormControl>
    );
};
